import { Injectable, inject } from '@angular/core';
import { DEFAULT_MAX_RESULT_COUNT, ZnSelectQueryParams } from '@zendra/shared';
import { DistributionService, FlowTypeService } from 'cbd-proxies/planning/proxy';
import {
  FinalizeStandFusionInputDto,
  FinalizeStandFusionOutputDto,
  StandFusionService,
  StandFusionStandDetailDto,
} from 'orchestrator';
import { forkJoin, map, Observable, switchMap } from 'rxjs';
import { resolveCatalogId } from '../../utils/catalog-resolution';

const STAND_FUSION_FLOW_TYPE_NAME = 'Fusión';
const NO_APPLICABLE_DISTRIBUTION_NAME = 'No aplica';
const PENDING_DISTRIBUTION_NAME = 'Pendiente';
const FINALIZATION_CATALOG_MAX_RESULT_COUNT = 20;

export type StandFusionFinalizeRequest = Omit<
  FinalizeStandFusionInputDto,
  'flowTypeId' | 'noApplicableDistributionId' | 'pendingDistributionId'
>;

@Injectable({
  providedIn: 'root',
})
export class StandFusionPlanningService {
  private readonly standFusionService = inject(StandFusionService);
  private readonly flowTypeService = inject(FlowTypeService);
  private readonly distributionService = inject(DistributionService);

  searchStandOptions(
    propertyId: number,
    params?: ZnSelectQueryParams,
  ): Observable<{ items: { key: string; value: string }[]; totalCount: number }> {
    return this.standFusionService
      .searchStands({
        propertyId,
        filter: params?.search,
        skipCount: params?.skipCount,
        maxResultCount: params?.maxResultCount ?? DEFAULT_MAX_RESULT_COUNT,
      })
      .pipe(
        map(response => ({
          items: response.items.map(item => ({
            key: String(item.id),
            value: item.name ?? String(item.id),
          })),
          totalCount: response.totalCount,
        })),
      );
  }

  validateStand(
    propertyId: number,
    standId: number,
    persistedStandId?: number | null,
  ): Observable<StandFusionStandDetailDto> {
    return this.standFusionService.validateStand({
      propertyId,
      standId,
      persistedStandId: persistedStandId ?? null,
    });
  }

  ensureAreaMaintenancePeriodAvailable(
    standId: number,
    periodId: number,
  ): Observable<void> {
    return this.standFusionService.ensureAreaMaintenancePeriodAvailable(standId, periodId);
  }

  finalize(input: StandFusionFinalizeRequest): Observable<FinalizeStandFusionOutputDto> {
    return forkJoin({
      flowTypes: this.flowTypeService.getList({
        name: STAND_FUSION_FLOW_TYPE_NAME,
        maxResultCount: FINALIZATION_CATALOG_MAX_RESULT_COUNT,
      }),
      noApplicableDistributions: this.distributionService.getList({
        name: NO_APPLICABLE_DISTRIBUTION_NAME,
        maxResultCount: FINALIZATION_CATALOG_MAX_RESULT_COUNT,
      }),
      pendingDistributions: this.distributionService.getList({
        name: PENDING_DISTRIBUTION_NAME,
        maxResultCount: FINALIZATION_CATALOG_MAX_RESULT_COUNT,
      }),
    }).pipe(
      map(({ flowTypes, noApplicableDistributions, pendingDistributions }) => ({
        flowTypeId: resolveCatalogId(
          flowTypes.items,
          STAND_FUSION_FLOW_TYPE_NAME,
          'FlowType',
        ),
        noApplicableDistributionId: resolveCatalogId(
          noApplicableDistributions.items,
          NO_APPLICABLE_DISTRIBUTION_NAME,
          'Distribution',
        ),
        pendingDistributionId: resolveCatalogId(
          pendingDistributions.items,
          PENDING_DISTRIBUTION_NAME,
          'Distribution',
        ),
      })),
      switchMap(catalogIds =>
        this.standFusionService.finalize({
          ...input,
          ...catalogIds,
        }),
      ),
    );
  }

}
